import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as AuthActions from './auth.actions';
import { catchError, map, mergeMap, take, tap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ToastService } from '@core/services/toast.service';
import { TranslationService } from 'src/app/services/translation';
import * as UserAuthActions from '../user/auth/user-auth.actions';
import { PlatformType } from '@core/models/platform.model';
import { APP_PATHS } from '@core/constants/platform-config.constants';
import { PlatformConfigService } from '@core/services/platform-config.service';
import { AuthService } from '@core/services/auth/auth.service';

export class AuthEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private router = inject(Router);
  private store = inject(Store);
  private toastService = inject(ToastService);
  private translationService = inject(TranslationService);
  private platformConfigService = inject(PlatformConfigService);

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      mergeMap(({ request }) =>
        this.authService.login(request).pipe(
          map((response) => AuthActions.loginSuccess({ response: response.body })),
          catchError((error) =>
            of(
              AuthActions.loginFailure({
                error: error?.error?.message || error?.message || 'Login failed',
              })
            )
          )
        )
      )
    )
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(() => {
          this.store.dispatch(UserAuthActions.resetUserAuth());
          this.store.dispatch(UserAuthActions.loadUserDetails());
        }),
        switchMap(({ response }) =>
          this.actions$.pipe(
            ofType(UserAuthActions.loadUserDetailsSuccess, UserAuthActions.loadUserDetailsFailure),
            take(1),
            tap((resultAction) => {
              if (resultAction.type === UserAuthActions.loadUserDetailsSuccess.type) {
                this.toastService.showSuccess('Login Successful', response.message);
                const accessiblePlatforms = this.platformConfigService.accessiblePlatforms();
                const technicianPlatform = accessiblePlatforms.find((p) => p.id === PlatformType.TC);
                if (technicianPlatform) {
                  this.router.navigate([technicianPlatform.defaultRoute]);
                } else {
                  this.router.navigate([`/${APP_PATHS.SELECT_PLATFORM}`]);
                }
              } else {
                this.toastService.showError('Access Denied', resultAction.error || 'Unauthorized access');
              }
            })
          )
        )
      ),
    { dispatch: false }
  );

  loginFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginFailure),
        tap(({ error }) => {
          this.toastService.showError(
            this.translationService.translate('auth.loginFailed'),
            error
          );
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(() => {
          this.authService.removeAccessToken();
          this.store.dispatch(UserAuthActions.resetUserAuth());
          this.router.navigate([`/${APP_PATHS.LOGIN}`]);
        })
      ),
    { dispatch: false }
  );
}
